/**
 * Custom subscription selector — Recharge CDN API + Shopify selling plans.
 * Owns product form selling_plan; does not depend on recharge-subscription-widget UI.
 */
(function () {
  'use strict';

  var CDN_BASE = 'https://static.rechargecdn.com/store';
  var CDN_VERSION = '2020-12';

  function normalizeText(s) {
    return String(s || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function shopDomain(el) {
    return el.dataset.shop || (window.Shopify && window.Shopify.shop) || '';
  }

  function readCatalogScript(el) {
    var id = el.dataset.catalogId;
    if (!id) return null;
    var node = document.getElementById(id);
    if (!node || !node.textContent) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function fetchRechargeCdn(shop, path) {
    return fetch(CDN_BASE + '/' + encodeURIComponent(shop) + path, {
      credentials: 'omit',
      cache: 'default',
    }).then(function (res) {
      if (!res.ok) throw new Error('cdn-' + res.status);
      return res.json();
    });
  }

  function loadRechargeProduct(shop, productId) {
    if (window.recharge && typeof window.recharge.getCDNProductAndSettings === 'function') {
      return window.recharge.getCDNProductAndSettings(productId).catch(function () {
        return loadRechargeProductFetch(shop, productId);
      });
    }
    return loadRechargeProductFetch(shop, productId);
  }

  function loadRechargeProductFetch(shop, productId) {
    return Promise.all([
      fetchRechargeCdn(shop, '/product/' + CDN_VERSION + '/' + productId + '.json'),
      fetchRechargeCdn(shop, '/' + CDN_VERSION + '/widget_settings.json').catch(function () {
        return { widget_settings: {} };
      }),
    ]).then(function (results) {
      return {
        product: results[0].product,
        widget_settings: results[1].widget_settings || {},
      };
    });
  }

  function findCdnVariant(product, variantId) {
    if (!product || !product.variants) return null;
    var id = String(variantId);
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      if (String(v.id) === id || String(v.external_variant_id) === id) return v;
    }
    return product.variants[0] || null;
  }

  function planNameFromCdn(product, planId) {
    var name = 'Subscription';
    if (!product || !product.selling_plan_groups) return name;
    product.selling_plan_groups.forEach(function (group) {
      (group.selling_plans || []).forEach(function (p) {
        if (String(p.selling_plan_id) === String(planId)) {
          name = p.selling_plan_name || name;
        }
      });
    });
    return name;
  }

  function buildModelFromCdn(data, variantId, labels) {
    var product = data.product;
    var widgetSettings = data.widget_settings || {};
    var variant = findCdnVariant(product, variantId);
    var purchaseMode =
      (product.subscription_options && product.subscription_options.storefront_purchase_options) ||
      'subscription_and_onetime';

    var showOnetime = purchaseMode !== 'subscription_only' && purchaseMode !== 'inactive';
    var showSubscribe = purchaseMode !== 'onetime_only' && purchaseMode !== 'inactive';
    var isSubscriptionFirst =
      widgetSettings.first_option === 'autodeliver' || widgetSettings.is_subscription_first === true;

    var plans = [];
    var seen = {};

    if (variant && variant.selling_plan_allocations) {
      variant.selling_plan_allocations.forEach(function (alloc) {
        var planId = alloc.selling_plan_id;
        if (!planId || seen[planId]) return;
        seen[planId] = true;
        plans.push({
          id: planId,
          name: planNameFromCdn(product, planId),
          price: alloc.discounted_price || '',
          compareAt: alloc.compare_at_price || '',
        });
      });
    }

    var onetime = { price: '', compareAt: '' };
    if (variant && variant.prices) {
      onetime.price = variant.prices.discounted_price || variant.prices.unit_price || '';
      onetime.compareAt = variant.prices.compare_at_price || '';
    }

    var defaultPurchase = isSubscriptionFirst && showSubscribe ? 'subscription' : 'onetime';
    if (!showOnetime) defaultPurchase = 'subscription';
    if (!showSubscribe) defaultPurchase = 'onetime';

    return {
      showOnetime: showOnetime,
      showSubscribe: showSubscribe,
      labels: labels,
      plans: plans,
      defaultPurchase: defaultPurchase,
      defaultPlanId: plans.length ? plans[0].id : null,
      onetime: onetime,
    };
  }

  function buildModelFromShopifyCatalog(catalog, variantId, labels) {
    var vid = String(variantId);
    var v = (catalog.variants && catalog.variants[vid]) || null;
    var plans = (v && v.plans) || [];
    var onetime = (v && v.onetime) || { price: '', compareAt: '' };

    return {
      showOnetime: true,
      showSubscribe: plans.length > 0,
      labels: labels,
      plans: plans.map(function (p) {
        return {
          id: p.id,
          name: p.name,
          price: p.price || '',
          compareAt: p.compareAt || '',
        };
      }),
      defaultPurchase: plans.length ? 'subscription' : 'onetime',
      defaultPlanId: plans.length ? plans[0].id : null,
      onetime: { price: onetime.price || '', compareAt: onetime.compareAt || '' },
    };
  }

  function mergeModels(shopifyModel, cdnModel) {
    if (!cdnModel) return shopifyModel;
    if (!shopifyModel) return cdnModel;

    var plans = shopifyModel.plans.length ? shopifyModel.plans : cdnModel.plans;
    return {
      showOnetime: cdnModel.showOnetime,
      showSubscribe: cdnModel.showSubscribe,
      labels: cdnModel.labels,
      plans: plans,
      defaultPurchase: cdnModel.defaultPurchase || shopifyModel.defaultPurchase,
      defaultPlanId:
        (cdnModel.defaultPlanId && plans.some(function (p) {
          return String(p.id) === String(cdnModel.defaultPlanId);
        })
          ? cdnModel.defaultPlanId
          : null) ||
        shopifyModel.defaultPlanId ||
        (plans[0] && plans[0].id),
      onetime: {
        price: shopifyModel.onetime.price || cdnModel.onetime.price,
        compareAt: shopifyModel.onetime.compareAt || cdnModel.onetime.compareAt,
      },
    };
  }

  function planById(plans, id) {
    if (!id) return null;
    return (
      plans.find(function (p) {
        return String(p.id) === String(id);
      }) || null
    );
  }

  function planByWeek(plans, weeks) {
    return (
      plans.find(function (p) {
        var n = normalizeText(p.name).toLowerCase();
        if (n.indexOf('week') < 0) return false;
        if (weeks === 12) return n.indexOf('12') >= 0;
        if (weeks === 4) return n.indexOf('4') >= 0 && n.indexOf('12') < 0;
        return false;
      }) || null
    );
  }

  /** Keep only 12-week, 4-week (by selling plan id), and one-time. */
  function applySupplyOptionsFilter(model, el) {
    if (!model) return model;

    var plans = model.plans || [];
    var plan12Id = el.dataset.plan12Week || '';
    var plan4Id = el.dataset.plan4Week || '';
    var p12 = planById(plans, plan12Id) || planByWeek(plans, 12);
    var p4 = planById(plans, plan4Id) || planByWeek(plans, 4);
    var onetimeOnly = el.dataset.onetimeOnly === 'true';
    var supplyOptions = [];

    if (!onetimeOnly) {
      if (p12) {
        supplyOptions.push({
          key: '12week',
          type: 'subscription',
          id: p12.id,
          label: el.dataset.label12Week || '12 week supply',
          price: p12.price,
          compareAt: p12.compareAt,
          cartQuantity: fallbackQuantityForSupplyKey('12week', el),
        });
      }
      if (p4) {
        supplyOptions.push({
          key: '4week',
          type: 'subscription',
          id: p4.id,
          label: el.dataset.label4Week || '4 week supply',
          price: p4.price,
          compareAt: p4.compareAt,
          cartQuantity: fallbackQuantityForSupplyKey('4week', el),
        });
      }
    }
    if (model.showOnetime !== false || onetimeOnly) {
      supplyOptions.push({
        key: 'onetime',
        type: 'onetime',
        id: null,
        label: el.dataset.labelOnetime || 'One-time purchase',
        price: (model.onetime && model.onetime.price) || '',
        compareAt: (model.onetime && model.onetime.compareAt) || '',
      });
    }

    model.supplyOptions = supplyOptions;
    model.plans = supplyOptions
      .filter(function (o) {
        return o.type === 'subscription';
      })
      .map(function (o) {
        return { id: o.id, name: o.label, price: o.price, compareAt: o.compareAt };
      });
    if (onetimeOnly) {
      model.defaultPlanId = null;
      model.defaultPurchase = 'onetime';
      model.showSubscribe = false;
    } else {
      model.defaultPlanId = p4 ? p4.id : p12 ? p12.id : null;
      model.defaultPurchase = p4 || p12 ? 'subscription' : 'onetime';
    }
    return model;
  }

  function parseMoneyAmount(s) {
    var n = parseFloat(String(s || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  }

  function formatMoneyAmount(amount) {
    var cfg = window.SubscriptionWidgetConfig && window.SubscriptionWidgetConfig.shop;
    var currency = (cfg && cfg.currency) || '';
    var formatted = amount.toFixed(2);
    if (currency === 'AUD' || currency === 'USD' || currency === 'CAD' || currency === 'NZD') {
      return '$' + formatted;
    }
    return formatted;
  }

  function addMoneyDisplay(discountedDisplay, savingsDisplay) {
    var discounted = parseMoneyAmount(discountedDisplay);
    var savings = parseMoneyAmount(savingsDisplay);
    if (discounted == null) return '';
    return formatMoneyAmount(discounted + (savings || 0));
  }

  function rechargeFormattedPricesForVariant(variantId) {
    var cfg = window.SubscriptionWidgetConfig && window.SubscriptionWidgetConfig.formattedPrices;
    return cfg && cfg[String(variantId)] ? cfg[String(variantId)] : null;
  }

  /** Bundle qty from Recharge quantity upsell total ÷ per-unit subscription price (e.g. 229.50 ÷ 76.50 = 3). */
  function quantityFromUpsellTotal(formatted, totalDisplay, perUnitDisplay) {
    var unit = parseMoneyAmount((formatted && formatted.discounted) || perUnitDisplay);
    var total = parseMoneyAmount(totalDisplay);
    if (unit > 0 && total > 0) {
      var q = Math.round(total / unit);
      if (q >= 1) return q;
    }
    return 1;
  }

  function fallbackQuantityForSupplyKey(key, el) {
    if (key === '12week') {
      var q12 = el && parseInt(el.dataset.plan12WeekQty, 10);
      return q12 >= 1 ? q12 : 3;
    }
    if (key === '4week') {
      var q4 = el && parseInt(el.dataset.plan4WeekQty, 10);
      return q4 >= 1 ? q4 : 1;
    }
    return 1;
  }

  function waitForRechargeFormattedPrices(variantId, cb) {
    if (rechargeFormattedPricesForVariant(variantId)) {
      cb();
      return;
    }
    var attempts = 0;
    var maxAttempts = 80;
    function tick() {
      if (rechargeFormattedPricesForVariant(variantId) || attempts >= maxAttempts) {
        cb();
        return;
      }
      attempts += 1;
      requestAnimationFrame(tick);
    }
    tick();
  }

  /** Recharge widget uses quantityUpsell totals (e.g. 12-week bundle), not per-unit allocation.price. */
  function enrichSupplyPricesFromRecharge(model, variantId, el) {
    if (!model) return model;
    var formatted = rechargeFormattedPricesForVariant(variantId);
    if (!formatted) return model;

    if (formatted.unit) {
      model.onetime = model.onetime || {};
      model.onetime.price = formatted.unit;
      model.onetime.compareAt = model.onetime.compareAt || '';
    }

    (model.supplyOptions || []).forEach(function (opt) {
      if (opt.type === 'onetime') {
        opt.price = formatted.unit || opt.price;
        opt.compareAt = '';
        return;
      }
      var sp = formatted.sellingPlans && formatted.sellingPlans[String(opt.id)];
      if (!sp) return;
      var upsell = sp.quantityUpsell;
      if (upsell && upsell.totalDiscountedPrice) {
        opt.price = upsell.totalDiscountedPrice;
        if (upsell.totalDiscountAmount) {
          opt.compareAt = addMoneyDisplay(upsell.totalDiscountedPrice, upsell.totalDiscountAmount);
        }
        opt.cartQuantity = quantityFromUpsellTotal(formatted, upsell.totalDiscountedPrice, sp.price);
        opt._bundlePriceFromRecharge = true;
      } else if (sp.price) {
        opt.price = sp.price;
        opt.cartQuantity = quantityFromUpsellTotal(formatted, sp.price, sp.price);
      }
      if (opt.type === 'subscription' && (!opt.cartQuantity || opt.cartQuantity < 1)) {
        opt.cartQuantity = fallbackQuantityForSupplyKey(opt.key, el);
      }
    });

    return model;
  }

  /** When Recharge widget prices are unavailable, scale per-unit Shopify prices to bundle totals (e.g. 12-week × 3). */
  function applyBundlePriceScaling(model, el) {
    if (!model || !model.supplyOptions) return model;

    var onetimeUnit = parseMoneyAmount(model.onetime && model.onetime.price);
    if (onetimeUnit == null) {
      onetimeUnit = parseMoneyAmount(model.onetime && model.onetime.compareAt);
    }

    (model.supplyOptions || []).forEach(function (opt) {
      if (opt.type !== 'subscription' || opt._bundlePriceFromRecharge) return;

      var qty =
        opt.cartQuantity >= 1 ? opt.cartQuantity : fallbackQuantityForSupplyKey(opt.key, el);
      if (qty <= 1) return;

      opt.cartQuantity = qty;
      var unitMain = parseMoneyAmount(opt.price);
      if (unitMain == null) return;

      opt.price = formatMoneyAmount(unitMain * qty);

      var unitCompare = parseMoneyAmount(opt.compareAt);
      if (unitCompare != null) {
        opt.compareAt = formatMoneyAmount(unitCompare * qty);
      } else if (onetimeUnit != null) {
        opt.compareAt = formatMoneyAmount(onetimeUnit * qty);
      }
    });

    return model;
  }

  function clearSellingPlanInputs(root) {
    if (!root) return;
    root.querySelectorAll('input[name="selling_plan"]').forEach(function (input) {
      input.remove();
    });
  }

  function ensureSellingPlanInput(form, planId) {
    clearSellingPlanInputs(form);
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'selling_plan';
    input.value = String(planId);
    form.appendChild(input);
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function supplyBenefits(supplyKey) {
    var guarantee =
      supplyKey === '4week' ? '30-day money-back guarantee' : '90-day money-back guarantee';
    return [
      'Free shipping on every order',
      guarantee,
      'Pause, swap or cancel anytime',
      'Designed for daily consistency',
    ];
  }

  function createBenefitsList(supplyKey) {
    var grid = document.createElement('ul');
    grid.className = 'hz-rc-custom-selector__benefits';
    supplyBenefits(supplyKey).forEach(function (text) {
      var li = document.createElement('li');
      li.className = 'hz-rc-custom-selector__benefit';
      var icon = document.createElement('span');
      icon.className = 'hz-rc-custom-selector__benefit-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8.5L6.5 12L13 4" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      var label = document.createElement('span');
      label.className = 'hz-rc-custom-selector__benefit-text';
      label.textContent = text;
      li.appendChild(icon);
      li.appendChild(label);
      grid.appendChild(li);
    });
    return grid;
  }

  var SUPPLY_META = {
    '12week': {
      mostPopular: false,
      subtitle: '90 pouches delivered every 12 weeks',
    },
    '4week': {
      mostPopular: true,
      subtitle: '30 pouches · Delivered every 30 days',
    },
    onetime: {
      subtitle: 'One time purchase of 30 pouches',
    },
  };

  function supplySubtitleForKey(key, el) {
    var fromDataset =
      key === '4week'
        ? el.dataset.subtitle4Week
        : key === '12week'
          ? el.dataset.subtitle12Week
          : key === 'onetime'
            ? el.dataset.subtitleOnetime
            : '';
    if (fromDataset) return fromDataset;
    var meta = SUPPLY_META[key];
    return (meta && meta.subtitle) || '';
  }

  function savingsBadgeText(compareAt, price) {
    var compare = parseMoneyAmount(compareAt);
    var main = parseMoneyAmount(price);
    if (compare != null && main != null && compare > main) {
      return 'SAVE ' + formatMoneyAmount(compare - main);
    }
    return '';
  }

  function unitPricePerServe(priceDisplay, cartQuantity, servesPerUnit) {
    var total = parseMoneyAmount(priceDisplay);
    var qty = cartQuantity >= 1 ? cartQuantity : 1;
    var perUnit = servesPerUnit >= 1 ? servesPerUnit : 30;
    var serves = qty * perUnit;
    if (total == null || serves < 1) return '';
    return formatMoneyAmount(total / serves) + ' / serve';
  }

  /** Weeks covered by a supply key (e.g. '12week' -> 12), 0 for one-time. */
  function weeksFromSupplyKey(key) {
    var m = /^(\d+)week$/.exec(key || '');
    return m ? parseInt(m[1], 10) : 0;
  }

  /** Per-day price for a subscription supply, derived from its full bundle price. */
  function perDayPriceText(priceDisplay, key, cartQuantity, servesPerUnit) {
    var total = parseMoneyAmount(priceDisplay);
    if (total == null) return '';
    var qty = cartQuantity >= 1 ? cartQuantity : 1;
    var perUnit = servesPerUnit >= 1 ? servesPerUnit : 30;
    var days = qty * perUnit;
    if (days < 1) {
      var weeks = weeksFromSupplyKey(key);
      if (weeks < 1) return '';
      days = weeks * 7;
    }
    return formatMoneyAmount(total / days) + ' / day';
  }

  function appendPriceBlock(parent, prices, unitServeText) {
    var wrap = document.createElement('div');
    wrap.className = 'hz-rc-custom-selector__card-prices';
    if (prices.compareAt) {
      var compare = document.createElement('span');
      compare.className = 'hz-rc-custom-selector__price-compare';
      compare.textContent = prices.compareAt;
      wrap.appendChild(compare);
    }
    if (prices.main) {
      var main = document.createElement('span');
      main.className = 'hz-rc-custom-selector__price-main';
      main.textContent = prices.main;
      wrap.appendChild(main);
    }
    if (unitServeText) {
      var unit = document.createElement('span');
      unit.className = 'hz-rc-custom-selector__price-unit';
      unit.textContent = unitServeText;
      wrap.appendChild(unit);
    }
    parent.appendChild(wrap);
  }

  function cloneComplimentaryGifts() {
    var tpl = document.getElementById('hz-complimentary-gifts-template');
    if (!tpl || !tpl.content || !tpl.content.firstElementChild) return null;
    return tpl.content.firstElementChild.cloneNode(true);
  }

  function HzRechargeCustomSelector(el) {
    el.hzRcInstance = this;
    this.el = el;
    this.model = null;
    this.state = { purchaseType: 'subscription', sellingPlanId: null };
    this._hzRcQtyVisible = false;
    this._hzRcInFormSync = false;
    this.panel = null;
    this.form = null;

    this.statusEl = el.querySelector('[data-hz-rc-status]');
    this.bodyEl = el.querySelector('[data-hz-rc-body]');
    this.errorEl = el.querySelector('[data-hz-rc-error]');
    this.errorTextEl = el.querySelector('[data-hz-rc-error-text]');
    this.purchaseTypesEl = el.querySelector('[data-hz-rc-purchase-types]');
    this.retryBtn = el.querySelector('[data-hz-rc-retry]');

    var self = this;
    if (this.retryBtn) {
      this.retryBtn.addEventListener('click', function () {
        self.load();
      });
    }

    this.load();
  }

  HzRechargeCustomSelector.prototype.getLabels = function () {
    return {
      onetime: this.el.dataset.labelOnetime || 'One-time purchase',
    };
  };

  HzRechargeCustomSelector.prototype.activeSupplyKey = function () {
    if (this.state.purchaseType === 'onetime') return 'onetime';
    var planId = String(this.state.sellingPlanId || '');
    var match = (this.model.supplyOptions || []).find(function (o) {
      return o.type === 'subscription' && String(o.id) === planId;
    });
    return match ? match.key : '';
  };

  HzRechargeCustomSelector.prototype.resolvePanel = function () {
    this.panel = this.el.closest('.recharge-supply-panel');
    this.form =
      (this.panel && this.panel.querySelector('form.wt-product__add-to-cart_form')) ||
      document.querySelector('[data-hz-recharge-host] form.wt-product__add-to-cart_form');
  };

  HzRechargeCustomSelector.prototype.showLoading = function () {
    if (this.statusEl) this.statusEl.hidden = false;
    if (this.bodyEl) this.bodyEl.hidden = true;
    if (this.errorEl) this.errorEl.hidden = true;
  };

  HzRechargeCustomSelector.prototype.showError = function (msg) {
    if (this._hzRcLoadWatchdog) {
      clearTimeout(this._hzRcLoadWatchdog);
      this._hzRcLoadWatchdog = null;
    }
    if (this.statusEl) this.statusEl.hidden = true;
    if (this.bodyEl) this.bodyEl.hidden = true;
    if (this.errorEl) this.errorEl.hidden = false;
    if (this.errorTextEl) this.errorTextEl.textContent = msg;
  };

  HzRechargeCustomSelector.prototype.showReady = function () {
    if (this._hzRcLoadWatchdog) {
      clearTimeout(this._hzRcLoadWatchdog);
      this._hzRcLoadWatchdog = null;
    }
    if (this.statusEl) this.statusEl.hidden = true;
    if (this.bodyEl) this.bodyEl.hidden = false;
    if (this.errorEl) this.errorEl.hidden = true;
    if (this.panel) {
      this.panel.setAttribute('data-hz-rc-custom-active', '');
      this.panel.querySelectorAll('.shopify-block, [id^="shopify-block"]').forEach(function (block) {
        block.setAttribute('inert', '');
        block.setAttribute('aria-hidden', 'true');
      });
    }
    this.syncBuyRowQuantityUi();
    try {
      this.el.dispatchEvent(new CustomEvent('hz-recharge:custom-ready', { bubbles: true }));
    } catch (e) {}
  };

  HzRechargeCustomSelector.prototype.initStateFromModel = function () {
    if (!this.model || !this.model.supplyOptions || !this.model.supplyOptions.length) return;
    var preferred =
      (this.model.defaultPurchase === 'onetime' &&
        this.model.supplyOptions.find(function (o) {
          return o.key === 'onetime';
        })) ||
      this.model.supplyOptions.find(function (o) {
        return o.key === '4week';
      }) ||
      this.model.supplyOptions.find(function (o) {
        return o.key === '12week';
      }) ||
      this.model.supplyOptions[0];

    if (preferred.type === 'onetime') {
      this.state.purchaseType = 'onetime';
      this.state.sellingPlanId = null;
    } else {
      this.state.purchaseType = 'subscription';
      this.state.sellingPlanId = preferred.id;
    }
  };

  HzRechargeCustomSelector.prototype.selectedSupplyOption = function () {
    var self = this;
    if (!this.model || !this.model.supplyOptions) return null;
    return (
      this.model.supplyOptions.find(function (o) {
        if (self.state.purchaseType === 'onetime') return o.key === 'onetime';
        return o.type === 'subscription' && String(o.id) === String(self.state.sellingPlanId);
      }) || null
    );
  };

  HzRechargeCustomSelector.prototype.render = function () {
    var self = this;
    var m = this.model;
    if (!m || !this.purchaseTypesEl) return;

    this.purchaseTypesEl.innerHTML = '';

    (m.supplyOptions || []).forEach(function (opt) {
      self.purchaseTypesEl.appendChild(self.createSupplyCard(opt));
    });

    this.applyFormSelection();
    this.updateUiState();
  };

  HzRechargeCustomSelector.prototype.createSupplyCard = function (opt) {
    var self = this;
    var isOnetime = opt.type === 'onetime';
    var selected = this.activeSupplyKey() === opt.key;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hz-rc-custom-selector__card' + (isOnetime ? ' hz-rc-custom-selector__card--onetime' : ' hz-rc-custom-selector__card--subscription');
    btn.dataset.supplyOption = opt.key;
    btn.dataset.purchaseType = isOnetime ? 'onetime' : 'subscription';
    if (!isOnetime) btn.dataset.sellingPlanId = String(opt.id);
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-checked', selected ? 'true' : 'false');
    if (selected) btn.classList.add('is-selected');

    if (isOnetime) {
      var onetimeOnlyLayout = this.el.dataset.onetimeOnly === 'true';
      var simpleTop = document.createElement('div');
      simpleTop.className = 'hz-rc-custom-selector__card-top';
      var radio = document.createElement('span');
      radio.className = 'hz-rc-custom-selector__radio';
      radio.setAttribute('aria-hidden', 'true');
      simpleTop.appendChild(radio);
      var simpleMain = document.createElement('div');
      simpleMain.className = 'hz-rc-custom-selector__card-main';
      var simpleTitleWrap = document.createElement('div');
      simpleTitleWrap.className = 'hz-rc-custom-selector__card-title';
      var simpleTitle = document.createElement('span');
      simpleTitle.className = 'hz-rc-custom-selector__card-title__label';
      simpleTitle.textContent = opt.label;
      simpleTitleWrap.appendChild(simpleTitle);
      simpleMain.appendChild(simpleTitleWrap);
      var onetimeMeta = SUPPLY_META.onetime || {};
      var onetimeSubtitle =
        supplySubtitleForKey('onetime', this.el) || onetimeMeta.subtitle || '';
      if (onetimeSubtitle && onetimeOnlyLayout) {
        var onetimeSubInline = document.createElement('p');
        onetimeSubInline.className = 'hz-rc-custom-selector__card-subtitle';
        onetimeSubInline.textContent = onetimeSubtitle;
        simpleMain.appendChild(onetimeSubInline);
      }
      simpleTop.appendChild(simpleMain);
      appendPriceBlock(simpleTop, { main: opt.price, compareAt: opt.compareAt }, '');
      btn.appendChild(simpleTop);

      if (onetimeSubtitle && !onetimeOnlyLayout) {
        var onetimeMetaRow = document.createElement('div');
        onetimeMetaRow.className = 'hz-rc-custom-selector__card-meta-row';
        var onetimeSub = document.createElement('p');
        onetimeSub.className = 'hz-rc-custom-selector__card-subtitle';
        onetimeSub.textContent = onetimeSubtitle;
        onetimeMetaRow.appendChild(onetimeSub);
        btn.appendChild(onetimeMetaRow);
      }
    } else {
      var meta = SUPPLY_META[opt.key] || {};
      var cardSubtitle = supplySubtitleForKey(opt.key, this.el) || meta.subtitle || '';
      var servesPerUnit = parseInt(this.el.dataset.servesPerUnit, 10) || 30;
      var unitServe = unitPricePerServe(opt.price, opt.cartQuantity, servesPerUnit);
      var savings = savingsBadgeText(opt.compareAt, opt.price);

      if (meta.mostPopular) {
        var banner = document.createElement('div');
        banner.className = 'hz-rc-custom-selector__card-banner';
        banner.setAttribute('aria-hidden', 'true');
        var bannerPopular = document.createElement('span');
        bannerPopular.className = 'hz-rc-custom-selector__card-banner-label';
        bannerPopular.textContent = 'Most popular';
        banner.appendChild(bannerPopular);
        if (savings) {
          var bannerSave = document.createElement('span');
          bannerSave.className = 'hz-rc-custom-selector__card-banner-save';
          bannerSave.textContent = savings;
          banner.appendChild(bannerSave);
        }
        btn.appendChild(banner);
      }

      var top = document.createElement('div');
      top.className = 'hz-rc-custom-selector__card-top';

      var radioSub = document.createElement('span');
      radioSub.className = 'hz-rc-custom-selector__radio';
      radioSub.setAttribute('aria-hidden', 'true');
      top.appendChild(radioSub);

      var main = document.createElement('div');
      main.className = 'hz-rc-custom-selector__card-main';

      var titleWrap = document.createElement('div');
      titleWrap.className = 'hz-rc-custom-selector__card-title';
      var titleEl = document.createElement('span');
      titleEl.className = 'hz-rc-custom-selector__card-title__label';
      titleEl.textContent = opt.label;
      titleWrap.appendChild(titleEl);
      main.appendChild(titleWrap);
      top.appendChild(main);

      appendPriceBlock(top, { main: opt.price, compareAt: opt.compareAt }, '');
      btn.appendChild(top);

      if (cardSubtitle || unitServe) {
        var metaRow = document.createElement('div');
        metaRow.className = 'hz-rc-custom-selector__card-meta-row';
        if (cardSubtitle) {
          var sub = document.createElement('p');
          sub.className = 'hz-rc-custom-selector__card-subtitle';
          sub.textContent = cardSubtitle;
          metaRow.appendChild(sub);
        }
        if (unitServe) {
          var unitMeta = document.createElement('span');
          unitMeta.className = 'hz-rc-custom-selector__price-unit';
          unitMeta.textContent = unitServe;
          metaRow.appendChild(unitMeta);
        }
        btn.appendChild(metaRow);
      }

      var expanded = document.createElement('div');
      expanded.className = 'hz-rc-custom-selector__card-expanded';
      var expandedInner = document.createElement('div');
      expandedInner.className = 'hz-rc-custom-selector__card-expanded-inner';
      var divider = document.createElement('hr');
      divider.className = 'hz-rc-custom-selector__card-divider';
      expandedInner.appendChild(divider);
      expandedInner.appendChild(createBenefitsList(opt.key));
      if (opt.key === '12week') {
        var gifts = cloneComplimentaryGifts();
        if (gifts) expandedInner.appendChild(gifts);
      }
      expanded.appendChild(expandedInner);
      btn.appendChild(expanded);
    }

    btn.addEventListener('click', function () {
      self.onSupplyOptionClick(opt);
    });
    return btn;
  };

  HzRechargeCustomSelector.prototype.updateUiState = function () {
    var self = this;
    var activeKey = this.activeSupplyKey();

    this.purchaseTypesEl.querySelectorAll('[data-supply-option]').forEach(function (card) {
      var on = card.dataset.supplyOption === activeKey;
      card.classList.toggle('is-selected', on);
      card.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    var selected = this.selectedSupplyOption();
    var activeKey = this.activeSupplyKey();
    var displayPrice = selected ? selected.price : '';
    // Subscription supplies (12-week / 4-week) show a per-day price on the
    // add-to-cart button; one-time purchases keep the full price.
    if (selected && this.state.purchaseType !== 'onetime') {
      var servesPerUnit = parseInt(this.el.dataset.servesPerUnit, 10) || 30;
      var perDay = perDayPriceText(
        selected.price,
        activeKey,
        selected.cartQuantity,
        servesPerUnit
      );
      if (perDay) displayPrice = perDay;
    }
    this.el.dataset.hzRcPurchaseType = this.state.purchaseType;
    this.el.dataset.hzRcSellingPlanId = this.state.sellingPlanId || '';
    this.el.dataset.hzRcDisplayPrice = normalizeText(displayPrice);
    this.el.dataset.hzRcDisplaySubtitle = normalizeText(
      selected ? supplySubtitleForKey(activeKey, this.el) : ''
    );
    this.el.dataset.hzRcDisplaySupplyPrice = normalizeText(
      selected && selected.price ? selected.price : ''
    );
    this.el.dataset.hzRcDisplaySupplyComparePrice = normalizeText(
      selected && selected.compareAt ? selected.compareAt : ''
    );
    this.syncBuyRowQuantityUi();
    this.dispatchPriceChange();
  };

  HzRechargeCustomSelector.prototype.supplyQuantityForSelection = function () {
    if (this.state.purchaseType === 'onetime') {
      var qtyInput = this.form && this.form.querySelector('input[name="quantity"]');
      var n = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return n >= 1 ? n : 1;
    }

    var selected = this.selectedSupplyOption();
    if (selected && selected.cartQuantity >= 1) {
      return selected.cartQuantity;
    }

    var variantId = this.el.dataset.variantId;
    var planId = this.state.sellingPlanId;
    var formatted = rechargeFormattedPricesForVariant(variantId);
    if (formatted && planId) {
      var sp = formatted.sellingPlans && formatted.sellingPlans[String(planId)];
      var upsell = sp && sp.quantityUpsell;
      if (upsell && upsell.totalDiscountedPrice) {
        return quantityFromUpsellTotal(formatted, upsell.totalDiscountedPrice, sp && sp.price);
      }
    }

    return fallbackQuantityForSupplyKey(this.activeSupplyKey(), this.el);
  };

  HzRechargeCustomSelector.prototype.applyCartQuantityToForm = function () {
    if (this._hzRcInFormSync) return;
    this.resolvePanel();
    if (!this.form) return;
    var qty = this.form.querySelector('input[name="quantity"]');
    if (!qty) return;
    var amount = this.supplyQuantityForSelection();
    var next = String(amount);
    if (qty.value === next) {
      this.el.dataset.hzRcCartQty = next;
      return;
    }
    this._hzRcInFormSync = true;
    try {
      qty.value = next;
      this.el.dataset.hzRcCartQty = next;
    } finally {
      this._hzRcInFormSync = false;
    }
  };

  HzRechargeCustomSelector.prototype.syncFormBeforeAdd = function () {
    if (!this.panel || !this.panel.hasAttribute('data-hz-rc-custom-active')) return;
    this.applyFormSelection();
  };

  HzRechargeCustomSelector.prototype.bindFormQuantityGuard = function () {
    var self = this;
    this.resolvePanel();
    if (!this.form) return;

    if (this.form.dataset.hzRcQtySubmitBound !== '1') {
      this.form.dataset.hzRcQtySubmitBound = '1';
      this.form.addEventListener(
        'submit',
        function () {
          self.syncFormBeforeAdd();
        },
        true
      );
    }

    var btn = this.form.querySelector('.js-add-to-cart');
    if (btn && btn.dataset.hzRcQtyClickBound !== '1') {
      btn.dataset.hzRcQtyClickBound = '1';
      btn.addEventListener(
        'click',
        function () {
          self.syncFormBeforeAdd();
        },
        true
      );
    }
  };

  HzRechargeCustomSelector.prototype.syncBuyRowQuantityUi = function () {
    var showQty = this.state.purchaseType === 'onetime';
    var prevShowQty = this._hzRcQtyVisible;
    this.resolvePanel();
    this.bindFormQuantityGuard();
    if (this.panel) {
      this.panel.setAttribute('data-hz-rc-show-qty', showQty ? 'true' : 'false');
    }
    if (!this.form) return;

    var qty = this.form.querySelector('input[name="quantity"]');
    if (showQty) {
      if (prevShowQty === false && qty) {
        qty.value = '1';
      }
    } else {
      this.applyCartQuantityToForm();
    }
    this._hzRcQtyVisible = showQty;
    if (qty) {
      this.el.dataset.hzRcCartQty = qty.value;
    }
  };

  HzRechargeCustomSelector.prototype.dispatchPriceChange = function () {
    try {
      this.el.dispatchEvent(new CustomEvent('hz-recharge:custom-selection-change', { bubbles: true }));
    } catch (e) {}
  };

  HzRechargeCustomSelector.prototype.onSupplyOptionClick = function (opt) {
    if (this.activeSupplyKey() === opt.key) return;
    if (opt.type === 'onetime') {
      this.state.purchaseType = 'onetime';
      this.state.sellingPlanId = null;
    } else {
      this.state.purchaseType = 'subscription';
      this.state.sellingPlanId = opt.id;
    }
    this.applyFormSelection();
    this.updateUiState();
  };

  HzRechargeCustomSelector.prototype.applyFormSelection = function () {
    if (this._hzRcInFormSync) return;
    this.resolvePanel();
    if (!this.form) return;

    this._hzRcInFormSync = true;
    try {
      if (this.state.purchaseType === 'onetime' || !this.state.sellingPlanId) {
        clearSellingPlanInputs(this.form);
        if (this.panel) clearSellingPlanInputs(this.panel);
      } else {
        ensureSellingPlanInput(this.form, this.state.sellingPlanId);
      }
      this.bindFormQuantityGuard();
    } finally {
      this._hzRcInFormSync = false;
    }

    this.applyCartQuantityToForm();
    this.bindFormSellingPlanGuard();
  };

  HzRechargeCustomSelector.prototype.bindFormSellingPlanGuard = function () {
    var self = this;
    this.resolvePanel();
    if (!this.form || this.form.dataset.hzRcSellingPlanGuard === '1') return;
    this.form.dataset.hzRcSellingPlanGuard = '1';
    this.form.addEventListener(
      'change',
      function (ev) {
        if (self._hzRcInFormSync) return;
        if (!self.panel || !self.panel.hasAttribute('data-hz-rc-custom-active')) return;
        var t = ev.target;
        if (!t || !t.form || t.form !== self.form) return;
        if (t.name === 'quantity' || t.name === 'id') return;
        if (t.name === 'selling_plan') {
          if (
            self.state.purchaseType !== 'onetime' &&
            String(t.value) !== String(self.state.sellingPlanId || '')
          ) {
            self.applyFormSelection();
          }
          return;
        }
        if (self.state.purchaseType === 'onetime' && self.form.querySelector('input[name="selling_plan"]')) {
          self.applyFormSelection();
        }
      },
      true
    );
  };

  HzRechargeCustomSelector.prototype.present = function (model) {
    var self = this;
    var variantId = this.el.dataset.variantId;
    var presentId = this._hzRcLoadId;

    function finish(sourceModel) {
      if (self._hzRcLoadId !== presentId) return;
      var nextModel = applySupplyOptionsFilter(sourceModel, self.el);
      nextModel = enrichSupplyPricesFromRecharge(nextModel, variantId, self.el);
      nextModel = applyBundlePriceScaling(nextModel, self.el);
      if (!nextModel || !nextModel.supplyOptions || !nextModel.supplyOptions.length) {
        self.showError('No supply options for this product.');
        return;
      }
      self.model = nextModel;
      self.initStateFromModel();
      self.render();
      self.showReady();
    }

    // Render immediately from Shopify/Recharge catalog — do not block on Recharge widget prices.
    finish(model);

    // Refresh card prices when the hidden Recharge app block publishes formattedPrices.
    waitForRechargeFormattedPrices(variantId, function () {
      finish(model);
    });
  };

  HzRechargeCustomSelector.prototype.resolveShopifyFallback = function (catalog, variantId, labels) {
    if (!catalog || !variantId) return null;
    var model = buildModelFromShopifyCatalog(catalog, variantId, labels);
    var filtered = applySupplyOptionsFilter(model, this.el);
    if (filtered.supplyOptions && filtered.supplyOptions.length) return model;
    return null;
  };

  HzRechargeCustomSelector.prototype.load = function () {
    var self = this;
    var productId = this.el.dataset.productId;
    var variantId = this.el.dataset.variantId;
    var shop = shopDomain(this.el);
    var labels = this.getLabels();
    var catalog = readCatalogScript(this.el);
    var loadId = (this._hzRcLoadId || 0) + 1;
    this._hzRcLoadId = loadId;

    this.showLoading();

    if (catalog && variantId) {
      var vPlans = (catalog.variants && catalog.variants[String(variantId)] && catalog.variants[String(variantId)].plans) || [];
      var p12FromCatalog = planByWeek(vPlans, 12);
      var p4FromCatalog = planByWeek(vPlans, 4);
      if (p12FromCatalog) this.el.dataset.plan12Week = String(p12FromCatalog.id);
      if (p4FromCatalog) this.el.dataset.plan4Week = String(p4FromCatalog.id);
    }

    var shopifyModel = self.resolveShopifyFallback(catalog, variantId, labels);
    if (shopifyModel) {
      self.present(shopifyModel);
    }

    if (self._hzRcLoadWatchdog) {
      clearTimeout(self._hzRcLoadWatchdog);
      self._hzRcLoadWatchdog = null;
    }

    self._hzRcLoadWatchdog = setTimeout(function () {
      if (self._hzRcLoadId !== loadId) return;
      if (self.bodyEl && !self.bodyEl.hidden) return;
      if (self.errorEl && !self.errorEl.hidden) return;
      var fallback = self.resolveShopifyFallback(catalog, variantId, labels);
      if (fallback) {
        self.present(fallback);
        return;
      }
      self.showError('Could not load subscription options. Check your connection and retry.');
    }, 6000);

    if (!shop || !productId) {
      if (!shopifyModel) {
        self.showError('Missing product configuration.');
      }
      return;
    }

    loadRechargeProduct(shop, productId)
      .then(function (data) {
        if (self._hzRcLoadId !== loadId) return;
        var cdnModel = buildModelFromCdn(data, variantId, labels);
        var merged = mergeModels(shopifyModel, cdnModel);
        var filteredMerged = applySupplyOptionsFilter(merged, self.el);
        if (filteredMerged.supplyOptions && filteredMerged.supplyOptions.length) {
          self.present(merged);
        } else if (shopifyModel) {
          self.present(shopifyModel);
        } else {
          self.showError('No subscription options returned from Recharge.');
        }
      })
      .catch(function () {
        if (self._hzRcLoadId !== loadId) return;
        if (shopifyModel) {
          self.present(shopifyModel);
        } else {
          self.showError('Could not load subscription options. Check your connection and retry.');
        }
      });
  };

  HzRechargeCustomSelector.prototype.onVariantChange = function (variantId) {
    var self = this;
    if (variantId) this.el.dataset.variantId = String(variantId);
    clearTimeout(this._hzRcVariantDebounce);
    this._hzRcVariantDebounce = setTimeout(function () {
      self.load();
    }, 80);
  };

  var initAllScheduled = false;
  function initAll() {
    if (initAllScheduled) return;
    initAllScheduled = true;
    requestAnimationFrame(function () {
      initAllScheduled = false;
      document.querySelectorAll('hz-recharge-custom-selector').forEach(function (el) {
        if (el.dataset.hzRcCustomInit === '1') return;
        el.dataset.hzRcCustomInit = '1';
        new HzRechargeCustomSelector(el);
      });
    });
  }

  function bindVariantListeners() {
    document.addEventListener(
      'variantChangeEnd',
      function (ev) {
        var source = ev.target;
        var variantId =
          (source && source.currentVariant && source.currentVariant.id) ||
          (source && source.dataset && source.dataset.variantId);
        if (!variantId) return;
        var host =
          (source && source.closest && source.closest('[data-hz-recharge-host]')) ||
          document.querySelector('[data-hz-recharge-host]');
        if (!host) return;
        host.querySelectorAll('hz-recharge-custom-selector').forEach(function (sel) {
          if (sel.hzRcInstance) sel.hzRcInstance.onVariantChange(variantId);
        });
      },
      true
    );

    document.addEventListener('change', function (ev) {
      var t = ev.target;
      if (!t || t.name !== 'id' || !t.form) return;
      var host = t.form.closest('[data-hz-recharge-host]');
      if (!host) return;
      host.querySelectorAll('hz-recharge-custom-selector').forEach(function (sel) {
        if (sel.hzRcInstance) sel.hzRcInstance.onVariantChange(t.value);
      });
    });

    document.addEventListener('flavourSelectionChange', function (ev) {
      var variantId = ev.detail && ev.detail.variantId;
      if (!variantId) return;
      var host =
        (ev.target && ev.target.closest && ev.target.closest('[data-hz-recharge-host]')) ||
        document.querySelector('.hz__home-preview[data-hz-recharge-host]');
      if (!host) return;
      host.querySelectorAll('hz-recharge-custom-selector').forEach(function (sel) {
        if (sel.hzRcInstance) sel.hzRcInstance.onVariantChange(variantId);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      bindVariantListeners();
    });
  } else {
    initAll();
    bindVariantListeners();
  }

  new MutationObserver(initAll).observe(document.documentElement, { childList: true, subtree: true });
})();
